import { ScrollArea } from "../../ScrollArea/index.js";
import { ListItemGroup } from "./ListItemGroup.js";
import { useEffect, useRef, useState } from "react";
import { IconLoader } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { useVirtualizer } from "@tanstack/react-virtual";
//#region src/Select/local-components/GroupsList.tsx
const ITEM_HEIGHT = 40;
const GROUP_HEADER_HEIGHT = 24;
const GroupsList = ({ groups, className, visibleItemsCount = 6, minVisibleItemsCount, search, onScrollEnd, hasNextPage, isFetching, defaultGroupsCollapsed }) => {
	const [openedGroups, setOpenedGroups] = useState({});
	const parentRef = useRef(null);
	const scrollEndRef = useRef(onScrollEnd);
	scrollEndRef.current = onScrollEnd;
	const defaultGroupOpen = !defaultGroupsCollapsed;
	const virtualizer = useVirtualizer({
		count: hasNextPage || isFetching ? groups.length + 1 : groups.length,
		getScrollElement: () => parentRef.current,
		estimateSize: (index) => {
			if (index === groups.length && (hasNextPage || isFetching)) return ITEM_HEIGHT;
			const group = groups[index];
			if (!group) return GROUP_HEADER_HEIGHT;
			const label = group.label;
			return openedGroups[label] ?? defaultGroupOpen ? group.items.length * ITEM_HEIGHT + GROUP_HEADER_HEIGHT : GROUP_HEADER_HEIGHT;
		},
		getItemKey: (index) => groups[index]?.id ?? groups[index]?.label ?? "loader_element",
		overscan: 2
	});
	const virtualItems = virtualizer.getVirtualItems();
	const lastVirtualItemIndex = virtualItems.at(-1)?.index;
	const toggleOpen = (label) => {
		setOpenedGroups((prev) => ({
			...prev,
			[label]: !(prev[label] ?? defaultGroupOpen)
		}));
	};
	useEffect(() => {
		if (hasNextPage && !isFetching && lastVirtualItemIndex === groups.length) scrollEndRef.current?.();
	}, [
		isFetching,
		hasNextPage,
		lastVirtualItemIndex,
		groups.length
	]);
	const maxViewportPx = visibleItemsCount * ITEM_HEIGHT + GROUP_HEADER_HEIGHT;
	const minViewportPx = minVisibleItemsCount ? Math.min(minVisibleItemsCount * ITEM_HEIGHT + GROUP_HEADER_HEIGHT, maxViewportPx) : 0;
	const viewportHeightPx = Math.max(minViewportPx, Math.min(virtualizer.getTotalSize(), maxViewportPx));
	return /* @__PURE__ */ jsx(ScrollArea, {
		ref: parentRef,
		direction: "vertical",
		rootClassName: cn("w-full", className),
		style: {
			height: `${viewportHeightPx}px`,
			maxHeight: `${maxViewportPx}px`
		},
		children: /* @__PURE__ */ jsx("ul", {
			className: "relative w-full",
			style: { height: `${virtualizer.getTotalSize()}px` },
			children: virtualItems?.map((virtualItem) => /* @__PURE__ */ jsxs("li", {
				"data-index": virtualItem.index,
				className: "absolute left-0 top-0 w-full",
				style: { transform: `translateY(${virtualItem.start}px)` },
				ref: virtualizer.measureElement,
				children: [(() => {
					const group = groups[virtualItem.index];
					if (virtualItem.index >= groups.length || !group) return null;
					return /* @__PURE__ */ jsx(ListItemGroup, {
						group,
						search,
						isOpen: openedGroups[group.label] ?? defaultGroupOpen,
						onToggleOpen: () => toggleOpen(group.label)
					});
				})(), virtualItem.index === groups.length && /* @__PURE__ */ jsx("div", {
					className: "flex h-10 cursor-progress flex-nowrap items-center justify-center",
					children: /* @__PURE__ */ jsx(IconLoader, { className: "size-6 animate-spin" })
				})]
			}, virtualItem.key))
		})
	});
};
//#endregion
export { GroupsList };

//# sourceMappingURL=GroupsList.js.map